import React, { useState } from 'react';
import moment from 'moment';
import queryString from 'query-string';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { backendUrl, tenantId } from 'config/envConfig';
import { checkAuthServerToken, decodeJWT } from 'contexts/helpers';
import __ from 'utils/i18n';
import { Form, CustomField } from 'components/forms';
import DateRangeInput from 'components/forms/inputs/DateRangeInput';
import ButtonLoader from 'containers/Dashboard/components/ButtonLoader/ButtonLoader';
import { validateExportDateRange } from '../../_shared/dateValidations';

export const MovementsExport = ({ 
  digitalAccountId,
  token,
  currentStartDate,
  currentEndDate,
  disabled = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      setErrorMessage(null);
    }
  };

  const handleDownload = async (values) => {
    const { startDate, endDate } = values.dateRange;
    const validationError = validateExportDateRange(startDate, endDate);
    
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setErrorMessage(null);
    setIsDownloading(true);

    const query = queryString.stringify({
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD'),
    });

    try {
      // Verificar y refrescar el token si es necesario antes de la descarga
      const featureFlags = decodeJWT(`geop_feature_flags_${tenantId}`);
      const jwtEnabled = featureFlags?.includes("auth_server_enabled") || featureFlags?.includes("keycloak_enabled");
      
      let authToken = token;
      
      if (jwtEnabled) {
        const newToken = await checkAuthServerToken();
        if (newToken) {
          authToken = `${newToken?.token_type} ${newToken?.access_token}`;
        }
      }

      const downloadResponse = await fetch(
        `${backendUrl}/proxy/api/v1/movements/${digitalAccountId}/download?${query}`,
        {
          method: 'GET',
          headers: {
            'x-Authentication-Token': authToken,
            'x-request-download': 'stream',
          },
        }
      );

      if (downloadResponse.status === 204) {
        setErrorMessage(__('dashboard.sections.movements.export.noDataError'));
        setIsDownloading(false);
        return;
      }

      if (downloadResponse.status === 401) {
        setErrorMessage(__('dashboard.sections.movements.export.authError'));
        setIsDownloading(false);
        // Redirigir al login después de un breve delay
        setTimeout(() => {
          window.location.href = '/logout';
        }, 2000);
        return;
      }

      if (downloadResponse.status > 300) {
        setErrorMessage(__('dashboard.sections.movements.export.downloadError'));
        setIsDownloading(false);
        return;
      }

      const reader = downloadResponse.body.getReader();
      const stream = new ReadableStream({
        start(controller) {
          async function pump() {
            const { done, value } = await reader.read();
            // When no more data needs to be consumed, close the stream
            if (done) {
              controller.close();
              return;
            }
            // Enqueue the next data chunk into our target stream
            controller.enqueue(value);
            return pump();
          }
          return pump();
        },
      });

      // Create a new response out of the stream
      const streamResponse = new Response(stream);
      const blob = await streamResponse.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `movements_${digitalAccountId}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toggleModal();
    } catch (error) {
      console.error('Error downloading movements:', error);
      setErrorMessage(__('dashboard.sections.movements.export.downloadError'));
    }

    setIsDownloading(false);
  };

  const defaultDateRange = {
    startDate: currentStartDate || moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: currentEndDate || moment().format('YYYY-MM-DD'),
  };

  return (
    <Form
      defaultValues={{
        dateRange: defaultDateRange,
      }}
    >
      {(form) => (
        <>
          <Button 
            className="buttom-radius" 
            onClick={() => {
              setIsModalOpen(true);
              setErrorMessage(null);
            }}
            disabled={isDownloading || disabled}
            title={disabled ? __('dashboard.sections.movements.export.disabledTooltip', 'Rango de fechas excede el máximo permitido') : ''}
          >
            {__('dashboard.sections.movements.export.button')}
          </Button>
          <Modal
            className="reportWidgetModal"
            isOpen={isModalOpen}
            toggle={toggleModal}
          >
            <ModalHeader>
              {__('dashboard.sections.movements.export.title')}
            </ModalHeader>
            <ModalBody>
              <div className="text-center">
                <CustomField 
                  field="dateRange" 
                  onChange={() => setErrorMessage(null)}
                >
                  <DateRangeInput past />
                </CustomField>
              </div>
              <div>
                {errorMessage && (
                  <div className="one-line-warning one-line-warning-transaction-report">
                    <i className="mdi mdi-information buttom-radius"></i>
                    <p>{errorMessage}</p>
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                className="buttom-radius"
                onClick={toggleModal}
              >
                {__('dashboard.sections.movements.export.cancel')}
              </Button>
              <ButtonLoader
                text={__('dashboard.sections.movements.export.download')}
                isLoading={isDownloading}
                onClick={() => {
                  handleDownload(form.values);
                }}
              />
            </ModalFooter>
          </Modal>
        </>
      )}
    </Form>
  );
};

export default MovementsExport;
